import React from "react";
import type { Key } from "react-aria";
import { c } from "ttag";

import type {
    ServerCmsModelsPagesDownloadLibraryPageAutocompleteEntry as AutocompleteEntry,
    ServerCmsTypesProductSummary as ProductSummary,
    ServerCmsModelsPagesMixinsDownloadLibraryTile as Tile,
    DownloadLibrarySearchBlock as Value,
} from "@reactivated";
import { reverse } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { Themed } from "../../styles/themes";
import { CenteredModal } from "../CenteredModal";
import { Clickable } from "../Clickables";
import { AutocompleteField } from "../Forms/Autocomplete";
import { ProductCard } from "../ProductCard";
import { DocumentList } from "../ProductDetails/DocumentList";
import { RichTextStreamBlock, firstHeadingId } from "../RichTextStreamBlock";
import { useOverflowGroup } from "../TwoUpCardsBlock";
import { TwoUpCard } from "../TwoUpCardsBlock/TwoUpCard";
import { CaptionedImageGrid } from "./CaptionedImageGrid";
import { DesignGuideModalTrigger } from "./DesignGuideModalTrigger";

import twoUpCardsStyles from "../TwoUpCardsBlock/index.module.scss";
import styles from "./index.module.scss";

interface LandingProps {
    value: Value;
    autocomplete: AutocompleteEntry[];
}

interface ProductProps extends LandingProps {
    product: Tile;
    /** Null when no summary resolves for the product. */
    productSummary: ProductSummary | null;
}

type Props = LandingProps | ProductProps;

/** Landing or product-detail view of the Download Library. */
export const DownloadLibrarySearchBlock = (props: Props) => {
    const { value, autocomplete } = props;
    const product = "product" in props ? props.product : undefined;
    const productSummary = "product" in props ? props.productSummary : null;

    const handleSelectionChange = (key: Key | null) => {
        if (!key) return;
        window.location.href = key.toString();
    };

    const [maxRatio, updateRatio] = useOverflowGroup();

    return (
        <Themed tag="section" theme="offWhite">
            <div className={styles.inner}>
                <div className={styles.search}>
                    {value.search_heading && (
                        <h2 className={styles.heading}>
                            {value.search_heading}
                        </h2>
                    )}
                    <AutocompleteField
                        label={value.search_placeholder ?? ""}
                        labelVisible={false}
                        items={autocomplete}
                        itemKey="url"
                        itemFormatter={(entry) =>
                            c("DownloadLibrary").t`${entry.sku}: ${entry.title}`
                        }
                        itemRender={(entry) => (
                            <div className={styles.item}>
                                <span className={styles.sku}>{entry.sku}</span>
                                <span className={styles.title}>
                                    {entry.title}
                                </span>
                            </div>
                        )}
                        onChange={handleSelectionChange}
                        placeholder={value.search_placeholder ?? ""}
                        defaultValue={product?.url}
                        className={styles.input}
                        popoverClassName={styles.popover}
                    />
                    <p className={styles.help}>
                        {value.search_subtext}
                        {value.search_subtext_cta && (
                            <CenteredModal
                                trigger={
                                    <Clickable className={styles.helpTrigger}>
                                        {value.search_subtext_cta}
                                    </Clickable>
                                }
                                theme="black"
                                panelClassName={styles.helpPanel}
                                labelledBy={firstHeadingId(
                                    value.modal_heading ?? null,
                                )}
                                label={value.search_subtext_cta ?? undefined}
                            >
                                {value.modal_heading && (
                                    <RichTextStreamBlock
                                        value={value.modal_heading}
                                    />
                                )}
                                {value.modal_images && (
                                    <CaptionedImageGrid
                                        items={value.modal_images}
                                    />
                                )}
                            </CenteredModal>
                        )}
                    </p>
                </div>

                {!product && (
                    <div
                        className={concatClassNames([
                            twoUpCardsStyles.grid,
                            styles.cards,
                        ])}
                    >
                        {value.card_one && (
                            <TwoUpCard
                                value={value.card_one}
                                maxRatio={maxRatio}
                                onOverflowChange={(ratio) =>
                                    updateRatio(0, ratio)
                                }
                                footer={
                                    <DesignGuideModalTrigger
                                        value={value.card_one}
                                    />
                                }
                            />
                        )}
                        {value.card_two && (
                            <TwoUpCard
                                value={value.card_two}
                                maxRatio={maxRatio}
                                onOverflowChange={(ratio) =>
                                    updateRatio(1, ratio)
                                }
                            />
                        )}
                    </div>
                )}

                {productSummary && (
                    <div className={styles.product}>
                        <ProductCard
                            product={productSummary}
                            layout="horizontalLarge"
                            showFavoriteButton={false}
                        />
                    </div>
                )}

                {product && (
                    <div className={styles.downloads}>
                        <DocumentList
                            value={product.documents}
                            downloadsUrl={reverse("cms-api:download-documents")}
                        />
                    </div>
                )}
            </div>
        </Themed>
    );
};
